import { Modal, ModalFooter } from './Modal';
import { Button } from './Button';
import { AlertTriangle, Trash2, HelpCircle, Info } from 'lucide-react';
import { useUIStore } from '@/stores';

export function ConfirmDialog() {
  const { confirmConfig, hideConfirm } = useUIStore();

  if (!confirmConfig) return null;

  const {
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    saveText = 'Save',
    variant = 'default',
    onConfirm,
    onCancel,
    onSave,
  } = confirmConfig;

  const handleConfirm = async () => {
    await onConfirm();
    hideConfirm();
  };

  const handleCancel = () => {
    onCancel?.();
    hideConfirm();
  };

  const handleSave = async () => {
    if (onSave) {
      await onSave();
      hideConfirm();
    }
  };

  const icons = {
    danger: <Trash2 className="h-6 w-6 text-red-600" />,
    warning: <AlertTriangle className="h-6 w-6 text-yellow-600" />,
    default: <HelpCircle className="h-6 w-6 text-primary-600" />,
    info: <Info className="h-6 w-6 text-blue-600" />,
  };

  const buttonVariants = {
    danger: 'danger',
    warning: 'primary',
    default: 'primary',
    info: 'secondary',
  } as const;

  // Use medium size when we have 3 buttons (onSave provided)
  const modalSize = onSave ? 'md' : 'sm';

  return (
    <Modal isOpen={true} onClose={handleCancel} size={modalSize} showCloseButton={false}>
      <div className="flex flex-col items-center text-center">
        <div className="mb-4">{icons[variant]}</div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-600">{message}</p>
      </div>
      <ModalFooter className="justify-center">
        {variant === 'info' ? (
          <Button variant="secondary" onClick={handleCancel}>
            {cancelText}
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={handleCancel}>
              {cancelText}
            </Button>
            <Button variant={buttonVariants[variant]} onClick={handleConfirm}>
              {confirmText}
            </Button>
            {onSave && (
              <Button variant="primary" onClick={handleSave}>
                {saveText}
              </Button>
            )}
          </>
        )}
      </ModalFooter>
    </Modal>
  );
}

// Hook for showing confirm dialogs
export function useConfirm() {
  const { showConfirm } = useUIStore();

  return {
    confirm: (config: {
      title: string;
      message: React.ReactNode;
      onConfirm: () => void | Promise<void>;
    }) => showConfirm({ ...config, variant: 'default' }),

    confirmDelete: (config: {
      title: string;
      message: React.ReactNode;
      onConfirm: () => void | Promise<void>;
    }) =>
      showConfirm({
        ...config,
        variant: 'danger',
        confirmText: 'Delete',
      }),

    confirmWarning: (config: {
      title: string;
      message: React.ReactNode;
      confirmText?: string;
      onConfirm: () => void | Promise<void>;
      onCancel?: () => void;
      onSave?: () => void | Promise<void>;
    }) =>
      showConfirm({
        ...config,
        variant: 'warning',
        confirmText: config.confirmText || 'Continue',
      }),
  };
}
